import { useEffect, useMemo, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { lastFirst, lastNameKey } from "@/lib/names";
import { StatusPill } from "@/components/StatusPill";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, X, Trash, CaretUp, CaretDown, CaretUpDown, PencilSimple } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import GeneratePreviewModal from "@/components/GeneratePreviewModal";
import { QuickView, QVRow, qvRowClick } from "@/components/QuickView";
import { useColumnPrefs } from "@/lib/useColumnPrefs";
import { ColumnChooser } from "@/components/ColumnChooser";

const TS_COLUMNS = [
  { key: "week_start", label: "Week", align: "left" },
  { key: "worker", label: "Worker", align: "left", locked: true },
  { key: "client_project", label: "Client / Project", align: "left" },
  { key: "total_hours", label: "Hours", align: "right" },
  { key: "per_diem_days", label: "Per Diem", align: "right" },
  { key: "supervisor", label: "Supervisor", align: "left" },
  { key: "status", label: "Status", align: "left" },
  { key: "invoiced", label: "Invoiced", align: "left" },
  { key: "reviewer", label: "Reviewed", align: "left" },
];

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const emptyHours = { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 };

function fmtLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function prevMondayISO() {
  const d = new Date();
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1) - day - 7);
  return fmtLocal(d);
}

function snapToMonday(dateStr) {
  if (!dateStr) return dateStr;
  const parts = dateStr.split("-").map(Number);
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  const wd = d.getDay();
  d.setDate(d.getDate() + (wd === 0 ? -6 : 1) - wd);
  return fmtLocal(d);
}

const supOf = (t) =>
  t.supervisor_name_snapshot || t.supervisor_email_snapshot ||
  t._project?.supervisor_name || t._project?.supervisor_email || "";

export default function Timesheets() {
  const { user } = useAuth();
  const readOnly = user.role !== "admin";
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("approved_pending");
  const [resendingId, setResendingId] = useState(null);
  const [sort, setSort] = useState({ col: "week_start", dir: "desc" });
  const [approveModal, setApproveModal] = useState({ open: false, tid: null, workerName: "", weekStart: "" });
  const [approveReason, setApproveReason] = useState("");
  const [approveBusy, setApproveBusy] = useState(false);
  const [excludeModal, setExcludeModal] = useState(null); // { ts, mode: "exclude" | "note" }
  const [exInvoices, setExInvoices] = useState([]);
  const [exInvoiceId, setExInvoiceId] = useState("");
  const [exNote, setExNote] = useState("");
  const [exBusy, setExBusy] = useState(false);
  const [viewT, setViewT] = useState(null);
  const { vis, hiddenSet, toggleColumn, resetColumns } = useColumnPrefs("timesheets", TS_COLUMNS);
  const visCount = TS_COLUMNS.length - hiddenSet.size + 1;

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    assignment_id: "",
    week_start: prevMondayISO(),
    hours: { ...emptyHours },
    per_diem_days: 0,
    exceptions: [],
    approval_mode: "send",
    manual_reason: "",
    billing_only: false,
    supervisor_email: "",
  });

  const load = () => api.get(`/timesheets${status ? `?status=${status}` : ""}`).then((r) => setRows(Array.isArray(r.data) ? r.data : []));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const toggleSort = (col) => setSort((s) => (s.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: col === "week_start" || col === "total_hours" || col === "per_diem_days" ? "desc" : "asc" }));

  const visibleRows = useMemo(() => {
    const getVal = (t) => {
      switch (sort.col) {
        case "week_start": return t.week_start || "";
        case "worker": return lastNameKey(t._worker?.name);
        case "client_project": return `${t._client?.name || ""} · ${t._project?.name || ""}`.toLowerCase();
        case "total_hours": return Number(t.total_hours) || 0;
        case "per_diem_days": return Number(t.per_diem_days) || 0;
        case "supervisor": return supOf(t).toLowerCase();
        case "status": return t.status || "";
        case "invoiced": return t.billing_excluded ? "!not-billed" : (t._invoiced_at || "");
        case "reviewer": return (t.reviewer || "").toLowerCase();
        default: return "";
      }
    };
    return [...rows].sort((a, b) => {
      const va = getVal(a), vb = getVal(b);
      if (va < vb) return sort.dir === "asc" ? -1 : 1;
      if (va > vb) return sort.dir === "asc" ? 1 : -1;
      // Stable secondary: newest week first, then worker name
      const wa = a.week_start || "";
      const wb = b.week_start || "";
      if (wa !== wb) return wa < wb ? 1 : -1;
      return (a._worker?.name || "").localeCompare(b._worker?.name || "");
    });
  }, [rows, sort]);
  useEffect(() => {
    if (readOnly) return;
    Promise.all([api.get("/assignments"), api.get("/workers"), api.get("/projects"), api.get("/clients")]).then(([a, w, p, c]) => {
      setAssignments(Array.isArray(a.data) ? a.data : []);
      setWorkers(Array.isArray(w.data) ? w.data : []);
      setProjects(Array.isArray(p.data) ? p.data : []);
      setClients(Array.isArray(c.data) ? c.data : []);
    });
  }, [readOnly]);

  const openGenerate = () => setShowPreview(true);
  const resend = async (tid) => {
    setResendingId(tid);
    try {
      const { data } = await api.post(`/timesheets/${tid}/resend-approval`);
      toast.success(`Approval link re-sent to ${data.supervisor_email}${data.sms_sent ? " (+ SMS)" : ""}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || e.message);
    } finally {
      setResendingId(null);
    }
  };

  const deleteTs = async (t) => {
    const label = `${t._worker?.name || "worker"} — week of ${t.week_start}`;
    const msg = t.status === "rejected"
      ? `Delete this REJECTED timesheet?\n\n${label}\n\nOnly this rejected entry is removed. Any other timesheet for the same week — including an approved resubmission — is a separate record and will NOT be affected.`
      : `Delete this timesheet?\n\n${label}\n\nThis cannot be undone. If the worker still needs to submit the correct hours, they can create a new timesheet for the same week after this is deleted.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/timesheets/${t.id}`);
      toast.success("Timesheet deleted.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || e.message);
    }
  };

  const openExcludeModal = async (t, mode) => {
    setExcludeModal({ ts: t, mode });
    setExInvoiceId(mode === "note" ? (t.covered_by_invoice_id || "") : "");
    setExNote(mode === "note" ? (t.variance_note || "") : "");
    try {
      const { data } = await api.get("/invoices");
      const list = (Array.isArray(data) ? data : [])
        .filter((i) => i.client_id === t.client_id && i.status !== "voided")
        .sort((a, b) => (b.week_ending || b.created_at || "").localeCompare(a.week_ending || a.created_at || ""));
      setExInvoices(list);
    } catch { setExInvoices([]); }
  };

  const submitExclude = async (e) => {
    e.preventDefault();
    const { ts, mode } = excludeModal;
    setExBusy(true);
    try {
      if (mode === "exclude") {
        await api.post(`/timesheets/${ts.id}/billing-exclude`, {
          excluded: true,
          covered_by_invoice_id: exInvoiceId || null,
          variance_note: exNote.trim() || null,
        });
        toast.success("Excluded from billing — pay run unaffected.");
      } else {
        await api.post(`/timesheets/${ts.id}/variance-note`, {
          covered_by_invoice_id: exInvoiceId || null,
          variance_note: exNote.trim() || null,
        });
        toast.success("Variance note saved.");
      }
      setExcludeModal(null);
      load();
    } catch (e2) { toast.error(e2.response?.data?.detail?.toString() || e2.message); }
    finally { setExBusy(false); }
  };

  const toggleExclude = async (t) => {
    if (!t.billing_excluded) { openExcludeModal(t, "exclude"); return; }
    const msg = `Re-include ${t._worker?.name || "this worker"}'s week of ${t.week_start} in billing? It will show as ready to invoice again${t.covered_by_invoice_number || t.variance_note ? ", and its variance note will be cleared" : ""}.`;
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/timesheets/${t.id}/billing-exclude`, { excluded: false });
      toast.success("Re-included in billing.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
  };

  const openManualApprove = (t) => {
    setApproveModal({ open: true, tid: t.id, workerName: t._worker?.name || "Worker", weekStart: t.week_start });
    setApproveReason("");
  };
  const submitManualApprove = async (e) => {
    e.preventDefault();
    if (!approveReason.trim()) return;
    setApproveBusy(true);
    try {
      await api.post(`/timesheets/${approveModal.tid}/admin-approve`, { reason: approveReason.trim() });
      toast.success(`Timesheet manually approved. Worker notified.`);
      setApproveModal({ open: false, tid: null, workerName: "", weekStart: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail?.toString() || err.message);
    } finally {
      setApproveBusy(false);
    }
  };

  const openNew = () => {
    setForm({ assignment_id: "", week_start: prevMondayISO(), hours: { ...emptyHours }, per_diem_days: 0, exceptions: [], approval_mode: "send", manual_reason: "", billing_only: false, supervisor_email: "" });
    setErr(""); setOpen(true);
  };

  const [weekModal, setWeekModal] = useState({ open: false, tid: null, workerName: "", current: "", value: "", status: "", payrun: false, dupNotice: "" });
  const [weekBusy, setWeekBusy] = useState(false);
  const openWeekEdit = (t) => setWeekModal({
    open: true, tid: t.id, workerName: t._worker?.name || "Worker",
    current: t.week_start, value: t.week_start, status: t.status, payrun: !!t.payrun_id, dupNotice: "",
  });
  const submitWeekEdit = async (e) => {
    e.preventDefault();
    setWeekBusy(true);
    try {
      const { data } = await api.patch(`/timesheets/${weekModal.tid}/week-start`,
        { week_start: weekModal.value, allow_duplicate: !!weekModal.dupNotice });
      toast.success(`Week updated to ${data.week_start}.`);
      setWeekModal((m) => ({ ...m, open: false }));
      load();
    } catch (err2) {
      const detail = err2.response?.data?.detail?.toString() || err2.message;
      if (err2.response?.status === 409) {
        setWeekModal((m) => ({ ...m, dupNotice: detail }));
      } else {
        toast.error(detail);
      }
    } finally { setWeekBusy(false); }
  };

  const totalHours = useMemo(
    () => DAYS.reduce((s, d) => s + (parseFloat(form.hours[d]) || 0), 0),
    [form.hours]
  );

  const selectedAssignment = assignments.find((a) => a.id === form.assignment_id);
  const selectedProject = projects.find((p) => p.id === selectedAssignment?.project_id);
  const selectedClient = clients.find((c) => c.id === selectedProject?.client_id);
  const selectedWorker = workers.find((w) => w.id === selectedAssignment?.worker_id);
  const projSups = selectedProject
    ? ((selectedProject.supervisors || []).filter((s) => s.email).length
        ? selectedProject.supervisors.filter((s) => s.email)
        : (selectedProject.supervisor_email
            ? [{ name: selectedProject.supervisor_name || "", email: selectedProject.supervisor_email }]
            : []))
    : [];

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const payload = {
        assignment_id: form.assignment_id,
        week_start: form.week_start,
        hours: form.hours,
        per_diem_days: parseInt(form.per_diem_days) || 0,
        exceptions: form.exceptions,
        manual_approve: form.approval_mode === "manual",
        manual_reason: form.approval_mode === "manual" ? form.manual_reason.trim() : null,
        billing_only: form.approval_mode === "manual" ? !!form.billing_only : false,
        supervisor_email: form.approval_mode === "send" ? (form.supervisor_email || null) : null,
      };
      await api.post("/timesheets", payload);
      toast.success(form.approval_mode === "manual"
        ? "Timesheet created and manually approved — no supervisor email sent."
        : "Timesheet submitted. Approval link sent to supervisor.");
      setOpen(false); load();
    } catch (e2) {
      setErr(e2.response?.data?.detail?.toString() || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 md:p-8">
      <div className="flex items-start justify-between mb-6">
        <div><div className="tinylabel">Operations</div><h1 className="text-3xl font-black">Timesheets</h1></div>
        <div className="flex gap-2">
          {!readOnly && (
            <button data-testid="new-timesheet-btn" onClick={openNew}
              className="h-11 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md flex items-center gap-2">
              <Plus size={18} /> New timesheet
            </button>
          )}
          <a data-testid="export-timesheets-csv" href={`${API_BASE}/exports/timesheets.csv`}
             className="h-11 px-4 border border-slate-300 rounded-md font-semibold hover:bg-slate-50 flex items-center">Export CSV</a>
          {!readOnly && (
            <button data-testid="generate-invoices-btn" onClick={openGenerate}
              className="h-11 px-4 border border-slate-300 hover:bg-slate-50 font-semibold rounded-md">
              Generate invoices from approved
            </button>
          )}
        </div>
      </div>
      <div className="mb-4 flex gap-2 items-center">
        <label className="tinylabel">Filter</label>
        <select data-testid="ts-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
          className="h-10 px-3 border border-slate-300 rounded-md bg-white">
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="approved_pending">Approved &amp; Pending</option>
          <option value="rejected">Rejected</option>
          <option value="invoiced">Invoiced</option>
          <option value="excluded">Excluded from billing</option>
          <option value="payrun">In Pay Run</option>
          <option value="on_voided_invoice">On voided invoice (never billed)</option>
        </select>
        <div className="ml-auto">
          <ColumnChooser columns={TS_COLUMNS} hiddenSet={hiddenSet} onToggle={toggleColumn} onReset={resetColumns} />
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              {TS_COLUMNS.filter((h) => vis(h.key)).map((h) => {
                const active = sort.col === h.key;
                const Icon = !active ? CaretUpDown : sort.dir === "asc" ? CaretUp : CaretDown;
                return (
                  <th key={h.key} className={`px-4 py-3 select-none ${h.align === "right" ? "text-right" : "text-left"}`}>
                    <button
                      type="button"
                      onClick={() => toggleSort(h.key)}
                      data-testid={`ts-sort-${h.key}`}
                      className={`inline-flex items-center gap-1 group ${active ? "text-slate-900" : "text-slate-500 hover:text-slate-900"}`}
                    >
                      <span className="uppercase tracking-widest text-xs font-bold">{h.label}</span>
                      <Icon size={12} weight={active ? "bold" : "regular"} className={active ? "opacity-100" : "opacity-40 group-hover:opacity-80"} />
                    </button>
                  </th>
                );
              })}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && (
              <tr>
                <td className="px-4 py-10" colSpan={visCount}>
                  <div data-testid="timesheets-empty-state" className="max-w-lg mx-auto text-center">
                    <div className="text-lg font-bold text-slate-800">
                      {status ? `No ${status} timesheets.` : "No timesheets yet."}
                    </div>
                    {!status && (
                      <>
                        <div className="text-sm text-slate-500 mt-2">
                          Timesheets show up here after a contractor submits weekly hours or an admin creates one on their behalf.
                        </div>
                        {!readOnly && (
                          <div className="mt-4 flex gap-2 justify-center flex-wrap">
                            <button
                              data-testid="empty-new-ts-btn"
                              onClick={openNew}
                              className="h-10 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md text-sm flex items-center gap-2"
                            >
                              <Plus size={16} weight="bold" /> Create the first timesheet
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            )}
            {visibleRows.map((t) => (
              <tr key={t.id} onClick={(e) => !readOnly && qvRowClick(e, () => setViewT(t))}
                  className={`border-t border-slate-100 hover:bg-slate-50 ${!readOnly ? "cursor-pointer" : ""}`} data-testid={`ts-row-${t.id}`}>
                {vis("week_start") && <td className="px-4 py-3">{t.week_start}</td>}
                <td className="px-4 py-3 font-medium">{lastFirst(t._worker?.name)}</td>
                {vis("client_project") && <td className="px-4 py-3 text-slate-600">{t._client?.name} · {t._project?.name}</td>}
                {vis("total_hours") && <td className="px-4 py-3 text-right font-mono">{t.total_hours}</td>}
                {vis("per_diem_days") && <td className="px-4 py-3 text-right font-mono">{t.per_diem_days}</td>}
                {vis("supervisor") && <td className="px-4 py-3 text-xs text-slate-600" data-testid={`ts-supervisor-${t.id}`}
                    title={t.supervisor_email_snapshot || t._project?.supervisor_email || ""}>
                  {supOf(t) || "—"}
                </td>}
                {vis("status") && <td className="px-4 py-3">
                  <div className="flex flex-col gap-1">
                    <StatusPill status={t.status} />
                    {t.status === "pending" && t._reminders && (
                      <span data-testid={`reminders-${t.id}`}
                        title={t._reminders.done
                          ? `Automatic reminders finished (${t._reminders.sent} sent${t._reminders.last_at ? `, last ${new Date(t._reminders.last_at).toLocaleString()}` : ""}). Use "Resend link" to remind the supervisor again.`
                          : `Automatic reminders go to the supervisor and the worker 3h, 5h and 8h after submission, then noon the next business day, then stop.${t._reminders.next_at ? ` Next: ${new Date(t._reminders.next_at).toLocaleString()}` : ""}`}
                        className={`text-[10px] leading-tight ${t._reminders.done ? "text-slate-400" : "text-amber-700"}`}>
                        {t._reminders.done
                          ? `Auto-reminders done (${t._reminders.sent}/${t._reminders.total})${t._reminders.manual ? " · resent manually" : ""}`
                          : `Auto-reminders ${t._reminders.sent}/${t._reminders.total}${t._reminders.next_at ? ` · next ${new Date(t._reminders.next_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} ${new Date(t._reminders.next_at).toLocaleDateString([], { weekday: "short" })}` : ""}`}
                      </span>
                    )}
                    {t.billing_only && (
                      <span data-testid={`billing-only-badge-${t.id}`} className="pill pill-billing-only">BILLING ONLY</span>
                    )}
                    {t.billing_excluded && (
                      <span data-testid={`billing-excluded-badge-${t.id}`} className="pill pill-excluded">NOT BILLED</span>
                    )}
                    {t.billing_excluded && (t.covered_by_invoice_number || t.variance_note) && (
                      <span data-testid={`covered-by-${t.id}`} className="text-[10px] text-slate-500 leading-tight"
                        title={t.variance_note || ""}>
                        {t.covered_by_invoice_number ? (
                          <>↳ covered by <Link to={`/invoices/${t.covered_by_invoice_id}`} className="underline font-semibold text-slate-600 hover:text-green-700">{t.covered_by_invoice_number}</Link></>
                        ) : (
                          <>↳ {t.variance_note}</>
                        )}
                      </span>
                    )}
                    {t.invoice_id && t.status === "approved" && (
                      <span data-testid={`draft-invoice-badge-${t.id}`} className="pill pill-draftinv">ON DRAFT INVOICE</span>
                    )}
                    {t.payrun_id && (
                      <span data-testid={`payrun-badge-${t.id}`} className="pill pill-payrun">PAY RUN</span>
                    )}
                  </div>
                </td>}
                {vis("invoiced") && <td className="px-4 py-3 text-xs whitespace-nowrap" data-testid={`invoiced-cell-${t.id}`}>
                  {t.billing_excluded ? (
                    <span className="font-bold text-purple-700">Not Billed</span>
                  ) : t._invoiced_at ? (
                    <>
                      <Link to={`/invoices/${t.invoice_id}`} title={t._invoice_number || ""}
                        className={`underline decoration-dotted ${t._invoice_voided ? "text-slate-400 line-through hover:text-slate-600" : "text-slate-600 hover:text-green-700"}`}>
                        {t._invoiced_at}
                      </Link>
                      {t._invoice_voided && <span className="ml-1 text-[10px] font-bold uppercase tracking-wider text-slate-500" title={`Invoice ${t._invoice_number || ""} was voided — this timesheet stays on it and will not be billed again.`} data-testid={`invoice-voided-tag-${t.id}`}>voided</span>}
                    </>
                  ) : (
                    <span className="text-slate-300">—</span>
                  )}
                </td>}
                {vis("reviewer") && <td className="px-4 py-3 text-slate-600 text-xs">{t.reviewer || "—"}{t.reject_reason ? ` · ${t.reject_reason}` : ""}</td>}
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-3">
                    {t.status === "pending" && !readOnly && (
                      <button
                        onClick={() => resend(t.id)}
                        disabled={resendingId === t.id}
                        data-testid={`resend-approval-${t.id}`}
                        className="text-green-600 hover:text-green-800 font-semibold text-xs disabled:opacity-60"
                      >{resendingId === t.id ? "Sending…" : "Resend link"}</button>
                    )}
                    {t.status === "pending" && !readOnly && (
                      <button
                        onClick={() => openManualApprove(t)}
                        data-testid={`manual-approve-${t.id}`}
                        className="text-amber-700 hover:text-amber-900 font-semibold text-xs border border-amber-200 bg-amber-50 hover:bg-amber-100 px-2 py-0.5 rounded"
                      >Manual approve</button>
                    )}
                    {!readOnly && t.status === "approved" && !t.invoice_id && !t.billing_only && (
                      <button
                        onClick={() => toggleExclude(t)}
                        data-testid={`billing-exclude-${t.id}`}
                        title={t.billing_excluded ? "Re-include in billing" : "Pay run only — exclude from billing"}
                        className={`font-semibold text-xs px-2 py-0.5 rounded border ${t.billing_excluded ? "text-slate-600 border-slate-300 bg-slate-50 hover:bg-slate-100" : "text-purple-700 border-purple-200 bg-purple-50 hover:bg-purple-100"}`}
                      >{t.billing_excluded ? "Re-include" : "Don't bill"}</button>
                    )}
                    {!readOnly && t.billing_excluded && (
                      <button
                        onClick={() => openExcludeModal(t, "note")}
                        data-testid={`variance-note-btn-${t.id}`}
                        title="Note which alternate invoice covered this week"
                        className="font-semibold text-xs px-2 py-0.5 rounded border text-slate-600 border-slate-300 bg-slate-50 hover:bg-slate-100"
                      >{t.covered_by_invoice_number || t.variance_note ? "Edit note" : "Add note"}</button>
                    )}
                    {!readOnly && (
                      <button
                        onClick={() => openWeekEdit(t)}
                        data-testid={`edit-week-${t.id}`}
                        title="Change week (admin correction)"
                        aria-label="Change week"
                        className="text-slate-400 hover:text-blue-600"
                      >
                        <PencilSimple size={16} weight="bold" />
                      </button>
                    )}
                    {!readOnly && t.status !== "invoiced" && (
                      <button
                        onClick={() => deleteTs(t)}
                        data-testid={`delete-ts-${t.id}`}
                        title="Delete timesheet"
                        aria-label="Delete timesheet"
                        className="text-slate-400 hover:text-red-600"
                      >
                        <Trash size={16} weight="bold" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showPreview && (
        <GeneratePreviewModal onClose={() => setShowPreview(false)} onGenerated={load} />
      )}

      {viewT && (
        <QuickView title={viewT._worker?.name || "Timesheet"}
                   subtitle={`${viewT._client?.name || "—"} · ${viewT._project?.name || "—"} — week of ${viewT.week_start}`}
                   status={viewT.status} onClose={() => setViewT(null)} testId="ts-quick-view">
          <div className="grid grid-cols-7 gap-1 mb-3">
            {DAYS.map((d) => (
              <div key={d} className="text-center border border-slate-100 rounded p-1.5">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{d}</div>
                <div className="font-mono text-sm">{Number(viewT.hours?.[d] || 0)}</div>
              </div>
            ))}
          </div>
          <QVRow label="Total hours" value={viewT.total_hours} mono />
          <QVRow label="Per-diem days" value={viewT.per_diem_days} mono />
          {(viewT.exceptions || []).length > 0 && (
            <QVRow label="Exceptions" value={viewT.exceptions.map((x) => `${x.description} ($${x.amount})`).join(", ")} />
          )}
          <QVRow label="Supervisor" value={supOf(viewT) ? `${supOf(viewT)}${viewT.supervisor_email_snapshot && supOf(viewT) !== viewT.supervisor_email_snapshot ? ` (${viewT.supervisor_email_snapshot})` : ""}` : null} />
          <QVRow label="Reviewed by" value={viewT.reviewer} />
          {viewT.approval_signature && (
            <div className="mt-2">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-400">Supervisor signature</div>
              <img src={viewT.approval_signature} alt="Supervisor signature" data-testid="qv-signature-img"
                   className="mt-1 border border-slate-200 rounded bg-white max-h-24" />
            </div>
          )}
          {viewT.reject_reason && <QVRow label="Reject reason" value={viewT.reject_reason} />}
          <QVRow label="Submitted" value={viewT.submitted_at ? new Date(viewT.submitted_at).toLocaleString() : null} />
          <QVRow label="Reviewed" value={viewT.reviewed_at ? new Date(viewT.reviewed_at).toLocaleString() : null} />
          <QVRow label="Invoiced" value={viewT.invoice_id ? "Yes" : "No"} />
          <QVRow label="In pay run" value={viewT.payrun_id ? "Yes" : "No"} />
          {!readOnly && (
            <button type="button" data-testid="qv-edit-week-btn"
              onClick={() => { const t = viewT; setViewT(null); openWeekEdit(t); }}
              className="mt-3 w-full h-10 border border-slate-300 rounded-md text-sm font-semibold hover:bg-slate-50 flex items-center justify-center gap-2">
              <PencilSimple size={14} weight="bold" /> Change week (admin correction)
            </button>
          )}
        </QuickView>
      )}

      {/* Manual Admin Approval Modal */}
      {excludeModal && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50">
          <form onSubmit={submitExclude} data-testid="exclude-modal"
            className="bg-white rounded-lg w-full max-w-md p-6 border border-slate-200 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  {excludeModal.mode === "exclude" ? "Exclude from billing" : "Billing variance note"}
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  {excludeModal.ts._worker?.name || "Worker"} — week of {excludeModal.ts.week_start}
                </p>
              </div>
              <button type="button" onClick={() => setExcludeModal(null)} className="text-slate-400 hover:text-slate-700">
                <X size={20} />
              </button>
            </div>
            {excludeModal.mode === "exclude" && (
              <div className="bg-purple-50 border border-purple-200 rounded-md px-3 py-2 mb-4 text-xs text-purple-800">
                The worker will STILL be paid in the pay run, but this week stops showing as
                "ready to invoice" and can't be attached to invoices. Use this when the client
                is billed via alternate (billing-only) timesheets.
              </div>
            )}
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">
              Covered by invoice <span className="normal-case font-normal text-slate-400">(optional — for the audit trail)</span>
            </label>
            <select value={exInvoiceId} onChange={(e) => setExInvoiceId(e.target.value)}
              data-testid="exclude-covered-invoice"
              className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md text-sm bg-white">
              <option value="">— None / not invoiced yet —</option>
              {exInvoices.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.number} · {i.week_ending ? `wk end ${i.week_ending} · ` : ""}${(i.total || 0).toLocaleString()} · {i.status}
                </option>
              ))}
            </select>
            <div className="text-xs text-slate-400 mt-1">
              {exInvoices.length === 0 ? "No invoices found for this client yet — you can add this later with the Add note button." : "Showing this client's invoices, newest week first."}
            </div>
            <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mt-4">
              Note <span className="normal-case font-normal text-slate-400">(optional)</span>
            </label>
            <textarea rows={2} value={exNote} onChange={(e) => setExNote(e.target.value)}
              data-testid="exclude-note-input"
              placeholder="e.g. Week split across two POs — billed on the alternate invoice"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm resize-none" />
            <div className="flex justify-end gap-3 mt-5">
              <button type="button" onClick={() => setExcludeModal(null)}
                className="h-10 px-4 border border-slate-300 rounded-md text-sm font-semibold hover:bg-slate-50">Cancel</button>
              <button type="submit" disabled={exBusy} data-testid="exclude-confirm-btn"
                className={`h-10 px-4 text-white font-semibold rounded-md text-sm disabled:opacity-50 ${excludeModal.mode === "exclude" ? "bg-purple-600 hover:bg-purple-700" : "bg-green-600 hover:bg-green-700"}`}>
                {exBusy ? "Saving…" : excludeModal.mode === "exclude" ? "Exclude from billing" : "Save note"}
              </button>
            </div>
          </form>
        </div>
      )}

      {approveModal.open && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50">
          <form onSubmit={submitManualApprove} className="bg-white rounded-lg w-full max-w-md p-6 border border-slate-200 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Manual Approval</h2>
                <p className="text-sm text-slate-500 mt-1">
                  {approveModal.workerName} — week of {approveModal.weekStart}
                </p>
              </div>
              <button type="button" onClick={() => setApproveModal({ open: false, tid: null, workerName: "", weekStart: "" })}
                      className="text-slate-400 hover:text-slate-700">
                <X size={20} />
              </button>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4 text-xs text-amber-800">
              This will approve the timesheet without electronic supervisor sign-off.
              Your reason will be saved permanently in the audit trail.
            </div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">
              Reason for manual approval <span className="text-red-500">*</span>
            </label>
            <textarea
              data-testid="manual-approve-reason"
              required
              autoFocus
              rows={3}
              value={approveReason}
              onChange={(e) => setApproveReason(e.target.value)}
              placeholder="e.g. Supervisor John Smith called and verbally approved on 08/11/2026"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button type="button"
                      onClick={() => setApproveModal({ open: false, tid: null, workerName: "", weekStart: "" })}
                      className="h-10 px-4 border border-slate-300 rounded-md text-sm font-semibold hover:bg-slate-50">
                Cancel
              </button>
              <button type="submit" disabled={approveBusy || !approveReason.trim()}
                      data-testid="manual-approve-submit"
                      className="h-10 px-4 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-md text-sm disabled:opacity-50">
                {approveBusy ? "Approving…" : "Approve timesheet"}
              </button>
            </div>
          </form>
        </div>
      )}

      {weekModal.open && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50">
          <form onSubmit={submitWeekEdit} data-testid="edit-week-modal"
                className="bg-white rounded-lg w-full max-w-md p-6 border border-slate-200 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Change week</h2>
                <p className="text-sm text-slate-500 mt-1">{weekModal.workerName} — currently week of {weekModal.current}</p>
              </div>
              <button type="button" onClick={() => setWeekModal((m) => ({ ...m, open: false }))}
                      className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4 text-xs text-amber-800">
              Admin correction: moves this timesheet to a different week. The supervisor approval
              stays intact — no new approval request is sent. The change is recorded in the audit trail.
            </div>
            {(weekModal.status === "invoiced" || weekModal.payrun) && (
              <div data-testid="edit-week-invoiced-warning" className="bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-4 text-xs text-red-700 font-semibold">
                This timesheet is already {weekModal.status === "invoiced" ? "on an invoice" : ""}
                {weekModal.status === "invoiced" && weekModal.payrun ? " and " : ""}
                {weekModal.payrun ? "in a pay run" : ""}. Those documents keep the old week —
                void / regenerate them if they must show the corrected date.
              </div>
            )}
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">New week starting (Mon)</label>
            <input required type="date" value={weekModal.value}
              onChange={(e) => setWeekModal((m) => ({ ...m, value: snapToMonday(e.target.value), dupNotice: "" }))}
              data-testid="edit-week-input"
              className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
            <div className="mt-1 text-xs text-slate-500">Snaps to the Monday of the week you pick.</div>
            {weekModal.dupNotice && (
              <div data-testid="edit-week-dup-warning" className="mt-3 bg-red-50 border-2 border-red-300 rounded-md px-3 py-2 text-xs text-red-800">
                <div className="font-bold">{weekModal.dupNotice}</div>
                <div className="mt-1">
                  If this duplicate is intentional — e.g. correcting a mis-dated timesheet that was
                  already processed/invoiced — click <b>Save anyway</b> to confirm. Both timesheets
                  will then show the same week for this assignment.
                </div>
              </div>
            )}
            <div className="flex justify-end gap-3 mt-5">
              <button type="button" onClick={() => setWeekModal((m) => ({ ...m, open: false }))}
                className="h-10 px-4 border border-slate-300 rounded-md text-sm font-semibold hover:bg-slate-50">Cancel</button>
              <button type="submit" disabled={weekBusy || !weekModal.value || weekModal.value === weekModal.current}
                data-testid="edit-week-save"
                className={`h-10 px-4 text-white font-semibold rounded-md text-sm disabled:opacity-50 ${weekModal.dupNotice ? "bg-red-600 hover:bg-red-700" : "bg-slate-900 hover:bg-slate-800"}`}>
                {weekBusy ? "Saving…" : weekModal.dupNotice ? "Save anyway — allow duplicate" : "Save new week"}
              </button>
            </div>
          </form>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <form onSubmit={submit} className="bg-white rounded-md w-full max-w-xl p-6 border border-slate-200 my-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">New timesheet</h2>
              <button type="button" onClick={() => setOpen(false)}><X size={20} /></button>
            </div>

            <div className="mb-4">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Assignment (worker · project)</label>
              <select required value={form.assignment_id}
                onChange={(e) => setForm({ ...form, assignment_id: e.target.value, supervisor_email: "" })}
                data-testid="new-ts-assignment"
                className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md bg-white">
                <option value="">Select…</option>
                {assignments.filter((a) => a.status === "active").map((a) => {
                  const w = workers.find((x) => x.id === a.worker_id);
                  const p = projects.find((x) => x.id === a.project_id);
                  return <option key={a.id} value={a.id}>{w?.name || "?"} · {p?.name || "?"}</option>;
                })}
              </select>
            </div>

            {selectedAssignment && (
              <div className="mb-4 border border-slate-200 rounded-md p-3 bg-slate-50 text-xs text-slate-600">
                <div><b>Client:</b> {selectedClient?.name}</div>
                <div><b>Project:</b> {selectedProject?.name} · PO {selectedProject?.po_number}</div>
                <div><b>Supervisor{projSups.length > 1 ? "s" : ""}:</b> {projSups.map((s) => s.name || s.email).join(", ") || "—"}</div>
                <div><b>Pay/Bill:</b> ${selectedAssignment.pay_rate}/hr · ${selectedAssignment.bill_rate}/hr · Per diem ${selectedAssignment.per_diem_rate}/day</div>
              </div>
            )}

            <div className="mb-4">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Week starting (Mon)</label>
              <input required type="date" value={form.week_start}
                onChange={(e) => setForm({ ...form, week_start: snapToMonday(e.target.value) })}
                data-testid="new-ts-week"
                className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
              <div className="mt-1 text-xs text-slate-500">Defaults to <b>last week</b> (Mon {prevMondayISO()}). Snaps to Monday.</div>
              {form.week_start !== prevMondayISO() && (
                <div data-testid="new-ts-week-note" className="mt-1 text-xs font-semibold text-amber-700">Heads-up: this is not last week's Monday.</div>
              )}
            </div>

            <div className="mb-4">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Hours per day</label>
              <div className="grid grid-cols-7 gap-2 mt-1">
                {DAYS.map((d) => (
                  <div key={d}>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 text-center">{d}</div>
                    <input type="number" min="0" step="0.25" value={form.hours[d]}
                      onChange={(e) => setForm({ ...form, hours: { ...form.hours, [d]: e.target.value } })}
                      data-testid={`new-ts-hours-${d}`}
                      className="mt-1 w-full h-11 px-2 border border-slate-300 rounded-md text-center font-mono" />
                  </div>
                ))}
              </div>
              <div className="mt-2 flex justify-between items-center text-sm">
                <span className="tinylabel">Total</span>
                <span data-testid="new-ts-total" className="text-xl font-black font-mono text-green-600">{totalHours.toFixed(2)}</span>
              </div>
            </div>

            <div className="mb-4">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Per-diem days</label>
              <input type="number" min="0" value={form.per_diem_days}
                onChange={(e) => setForm({ ...form, per_diem_days: e.target.value })}
                data-testid="new-ts-per-diem"
                className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md font-mono" />
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Exceptions (optional)</label>
                <button type="button" onClick={() => setForm({ ...form, exceptions: [...form.exceptions, { description: "", amount: 0 }] })}
                  data-testid="new-ts-add-exception"
                  className="text-green-600 font-semibold flex items-center gap-1 text-sm"><Plus size={14} /> Add</button>
              </div>
              {form.exceptions.map((ex, i) => (
                <div key={i} className="flex gap-2 items-center mt-2">
                  <input placeholder="Description" value={ex.description}
                    onChange={(e) => {
                      const c = [...form.exceptions]; c[i] = { ...c[i], description: e.target.value };
                      setForm({ ...form, exceptions: c });
                    }}
                    className="flex-1 h-10 px-3 border border-slate-300 rounded-md" />
                  <input type="number" step="0.01" placeholder="$" value={ex.amount}
                    onChange={(e) => {
                      const c = [...form.exceptions]; c[i] = { ...c[i], amount: parseFloat(e.target.value) || 0 };
                      setForm({ ...form, exceptions: c });
                    }}
                    className="w-24 h-10 px-3 border border-slate-300 rounded-md" />
                  <button type="button" onClick={() => setForm({ ...form, exceptions: form.exceptions.filter((_, j) => j !== i) })}
                    className="p-2 text-red-600"><Trash size={18} /></button>
                </div>
              ))}
            </div>

            <div className="mb-4">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Approval</label>
              <div className="mt-2 space-y-2">
                <label className={`flex items-start gap-2 border rounded-md p-3 cursor-pointer ${form.approval_mode === "send" ? "border-green-500 bg-green-50/50" : "border-slate-200"}`}>
                  <input type="radio" name="approval-mode" className="mt-0.5" data-testid="approval-mode-send"
                    checked={form.approval_mode === "send"} onChange={() => setForm({ ...form, approval_mode: "send" })} />
                  <span>
                    <span className="block text-sm font-semibold">Send to supervisor for approval</span>
                    <span className="block text-xs text-slate-500">Emails the approval link to the project supervisor.</span>
                  </span>
                </label>
                {form.approval_mode === "send" && projSups.length > 1 && (
                  <div className="ml-6">
                    <select required value={form.supervisor_email}
                      onChange={(e) => setForm({ ...form, supervisor_email: e.target.value })}
                      data-testid="new-ts-supervisor"
                      className={`w-full h-11 px-3 border rounded-md bg-white ${form.supervisor_email ? "border-slate-300" : "border-amber-400"}`}>
                      <option value="">Select approving supervisor…</option>
                      {projSups.map((s) => (
                        <option key={s.email} value={s.email}>{s.name ? `${s.name} (${s.email})` : s.email}</option>
                      ))}
                    </select>
                    {!form.supervisor_email && (
                      <div className="mt-1 text-xs font-semibold text-amber-700">This project has multiple supervisors — pick who gets the approval email.</div>
                    )}
                  </div>
                )}
                <label className={`flex items-start gap-2 border rounded-md p-3 cursor-pointer ${form.approval_mode === "manual" ? "border-amber-500 bg-amber-50/50" : "border-slate-200"}`}>
                  <input type="radio" name="approval-mode" className="mt-0.5" data-testid="approval-mode-manual"
                    checked={form.approval_mode === "manual"} onChange={() => setForm({ ...form, approval_mode: "manual" })} />
                  <span>
                    <span className="block text-sm font-semibold">Manually approve now</span>
                    <span className="block text-xs text-slate-500">No supervisor email — use when you already have a signed paper timesheet.</span>
                  </span>
                </label>
                {form.approval_mode === "manual" && (
                  <>
                    <input required value={form.manual_reason}
                      onChange={(e) => setForm({ ...form, manual_reason: e.target.value })}
                      placeholder="Reason (required) — e.g. Signed paper timesheet on file"
                      data-testid="manual-reason-input"
                      className="w-full h-11 px-3 border border-amber-300 rounded-md" />
                    <label className={`flex items-start gap-2 border rounded-md p-3 cursor-pointer ${form.billing_only ? "border-purple-400 bg-purple-50/60" : "border-slate-200"}`} data-testid="billing-only-toggle">
                      <input type="checkbox" className="mt-0.5" data-testid="billing-only-checkbox"
                        checked={!!form.billing_only} onChange={(e) => setForm({ ...form, billing_only: e.target.checked })} />
                      <span>
                        <span className="block text-sm font-semibold text-purple-800">Billing only — exclude from pay run</span>
                        <span className="block text-xs text-slate-500">For special-circumstance client billing (splitting or reallocating hours between invoices). The worker is still paid from their original timesheet — this one exists only to attach to an invoice. Duplicate weeks are allowed.</span>
                      </span>
                    </label>
                  </>
                )}
              </div>
            </div>

            {err && <div className="text-red-600 text-sm mb-3">{err}</div>}
            <div className="mt-4 flex gap-3 justify-end">
              <button type="button" onClick={() => setOpen(false)}
                className="h-11 px-4 border border-slate-300 rounded-md font-semibold">Cancel</button>
              <button data-testid="submit-new-timesheet-btn" disabled={busy || !form.assignment_id}
                className="h-11 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md disabled:opacity-60">
                {busy ? "Submitting…" : form.approval_mode === "manual" ? "Create & approve" : "Submit timesheet"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
